import React, { useEffect, useRef } from 'react'
import { X, CheckCircle, AlertTriangle, AlertCircle, Plus } from 'lucide-react'
import { cn } from '@/utils/cn'
import { SectionValidationResult } from '@/types'
import { useCategoryContext } from '@/contexts/CategoryContext'
import { getClassColor } from '@/utils/colorUtils'
import { formatClassNameForDisplay } from '@/utils/classNameUtils'
import AuthenticatedImage from '@/components/AuthenticatedImage'

interface MobileValidationResultsProps {
  isOpen: boolean
  onClose: () => void
  validationResult: SectionValidationResult | null
}

const MobileValidationResults: React.FC<MobileValidationResultsProps> = ({
  isOpen,
  onClose,
  validationResult,
}) => {
  const { getClassCategory, getClassThumbnail, refreshThumbnailForClass } = useCategoryContext()

  // Track which classes we've attempted to load thumbnails for
  const attemptedLoads = useRef<Set<string>>(new Set())

  // Load thumbnails on-demand for classes that don't have them cached
  useEffect(() => {
    if (!validationResult) return

    validationResult.validation_items.forEach(item => {
      const thumb = getClassThumbnail(item.class_name)
      if (!thumb && !attemptedLoads.current.has(item.class_name)) {
        attemptedLoads.current.add(item.class_name)
        refreshThumbnailForClass(item.class_name)
      }
    })
  }, [validationResult, getClassThumbnail, refreshThumbnailForClass])

  if (!isOpen || !validationResult) return null

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col safe-area-inset">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-white flex-shrink-0">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold text-gray-900">Validation Results</h2>
          <p className="text-sm text-gray-500 truncate">{validationResult.section_name}</p>
        </div>
        <button
          onClick={onClose}
          className="p-2 -mr-2 text-gray-500 hover:text-gray-700 active:bg-gray-100 rounded-lg flex-shrink-0"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      {/* Status Summary */}
      <div className="px-4 py-3 border-b flex-shrink-0">
        {validationResult.has_discrepancies ? (
          <div className="flex items-center gap-2 text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
            <span className="font-medium text-sm">Validation issues detected</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 px-3 py-2 rounded-lg">
            <CheckCircle className="h-5 w-5 flex-shrink-0" />
            <span className="font-medium text-sm">All expected items match</span>
          </div>
        )}
      </div>

      {/* Items List */}
      <div className="flex-1 overflow-y-auto">
        {validationResult.validation_items.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <AlertCircle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No items to validate</p>
            <p className="text-sm mt-1">
              This section has no expected items defined
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {validationResult.validation_items.map((item) => {
              const difference = item.detected - item.expected
              const category = getClassCategory(item.class_name)
              const color = getClassColor(item.class_name, category)
              const thumbnail = getClassThumbnail(item.class_name)?.url

              // Determine status styling
              let statusBgColor = ''
              let statusTextColor = ''
              let statusIcon = null
              let statusText = ''

              switch (item.status) {
                case 'match':
                  statusBgColor = 'bg-emerald-100'
                  statusTextColor = 'text-emerald-700'
                  statusIcon = <CheckCircle className="h-4 w-4" />
                  statusText = 'Match'
                  break
                case 'mismatch':
                  statusBgColor = 'bg-amber-100'
                  statusTextColor = 'text-amber-700'
                  statusIcon = <AlertTriangle className="h-4 w-4" />
                  statusText = difference > 0 ? `+${difference}` : `${difference}`
                  break
                case 'missing':
                  statusBgColor = 'bg-red-100'
                  statusTextColor = 'text-red-700'
                  statusIcon = <AlertCircle className="h-4 w-4" />
                  statusText = `${difference}`
                  break
                case 'unexpected':
                  statusBgColor = 'bg-blue-100'
                  statusTextColor = 'text-blue-700'
                  statusIcon = <Plus className="h-4 w-4" />
                  statusText = `+${difference}`
                  break
              }

              return (
                <div
                  key={item.class_name}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3',
                    item.status === 'match' ? 'bg-emerald-50/50' :
                    item.status === 'mismatch' ? 'bg-amber-50/50' :
                    item.status === 'missing' ? 'bg-red-50/50' :
                    'bg-blue-50/50'
                  )}
                >
                  {/* Thumbnail */}
                  {thumbnail ? (
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                      <AuthenticatedImage
                        imageUrl={thumbnail}
                        alt={item.class_name}
                        size="thumb"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div
                      className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center"
                      style={{ backgroundColor: `${color}20` }}
                    >
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: color }}
                      />
                    </div>
                  )}

                  {/* Class Name */}
                  <div className="flex-1 min-w-0">
                    <p
                      className="font-medium text-sm truncate"
                      style={{ color }}
                    >
                      {formatClassNameForDisplay(item.class_name)}
                    </p>
                  </div>

                  {/* Status Badge */}
                  <div
                    className={cn(
                      'flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0',
                      statusBgColor,
                      statusTextColor
                    )}
                  >
                    {statusIcon}
                    <span>{statusText}</span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-4 border-t bg-gray-50 safe-area-bottom flex-shrink-0">
        <button
          onClick={onClose}
          className={cn(
            'w-full py-3 rounded-xl font-medium',
            'bg-gray-200 text-gray-700',
            'active:bg-gray-300 transition-colors'
          )}
        >
          Done
        </button>
      </div>
    </div>
  )
}

export default MobileValidationResults
